import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
import { useAppRegistry } from '../../core/contexts/AppRegistry';
export function Dock() {
  const {
    t,
    theme: currentTheme
  } = useTheme();
  const {
    windows,
    openWindow,
    focusWindow,
    minimizeWindow,
    isMobile
  } = useWindows();
  const {
    apps
  } = useAppRegistry();
  const isLightTheme = currentTheme === 'light';
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  // Mouse position tracking for magnification effect
  const mouseX = useMotionValue(Infinity);
  const mainApps = apps.filter(app => app.category === 'main');
  const handleAppClick = (appId: string, appTitle: string) => {
    const appWindows = windows.filter(w => w.appId === appId);
    if (appWindows.length === 0) {
      openWindow(appId, appTitle);
      return;
    }
    const activeWindow = appWindows.reduce((prev, current) => current.zIndex > prev.zIndex ? current : prev);
    if (activeWindow.isMinimized) {
      minimizeWindow(activeWindow.id);
      return;
    }
    const maxZIndex = Math.max(...windows.map(w => w.zIndex));
    const isTopWindow = activeWindow.zIndex === maxZIndex;
    if (isTopWindow) {
      minimizeWindow(activeWindow.id);
    } else {
      focusWindow(activeWindow.id);
    }
  };
  const getWindowCount = (appId: string) => {
    return windows.filter(w => w.appId === appId && !w.isMinimized).length;
  };
  // Mobile: Fixed Bottom Bar
  if (isMobile) {
    return <motion.div initial={{
      y: 100
    }} animate={{
      y: 0
    }} transition={{
      type: 'spring',
      bounce: 0.15,
      duration: 0.5
    }} className={`fixed bottom-0 left-0 right-0 backdrop-blur-2xl px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-around z-[9999] border-t ${isLightTheme ? 'bg-white/80 border-black/[0.06]' : 'bg-[#1A1A1A]/80 border-white/[0.06]'}`} data-id="element-207">
        {mainApps.map(app => {
        const windowCount = getWindowCount(app.id);
        const Icon = app.icon;
        const isActive = windowCount > 0;
        return <button key={app.id} onClick={() => handleAppClick(app.id, t(`${app.id}.title`))} className="relative p-3 flex flex-col items-center gap-1" data-id="element-208">
              <Icon className={`w-6 h-6 ${isActive ? isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]' : isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-209" />
              {isActive && <div className="absolute bottom-1 w-1 h-1 rounded-full bg-[#C8956C]" data-id="element-210" />}
            </button>;
      })}
      </motion.div>;
  }
  // Desktop: Floating Dock
  return <motion.div initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    bounce: 0.15,
    duration: 0.6,
    delay: 0.2
  }} className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center z-[9999]" data-id="element-211">
      <div onMouseMove={e => {
      mouseX.set(e.pageX);
    }} onMouseLeave={() => {
      mouseX.set(Infinity);
    }} className={`backdrop-blur-2xl rounded-2xl px-3 py-2.5 flex items-center gap-2 border shadow-2xl ${isLightTheme ? 'bg-white/80 border-black/[0.06] shadow-black/[0.04]' : 'bg-[#1A1A1A]/80 border-white/[0.06] shadow-black/40'}`} data-id="element-212">
        {mainApps.map((app, index) => {
        const windowCount = getWindowCount(app.id);
        const Icon = app.icon;
        const isActive = windowCount > 0;
        return <DockIcon key={app.id} app={app} mouseX={mouseX} isActive={isActive} hoveredApp={hoveredApp} setHoveredApp={setHoveredApp} handleAppClick={handleAppClick} isLightTheme={isLightTheme} t={t} data-id="element-213" />;
      })}
      </div>
    </motion.div>;
}
// New component for individual dock icon with magnification
function DockIcon({
  app,
  mouseX,
  isActive,
  hoveredApp,
  setHoveredApp,
  handleAppClick,
  isLightTheme,
  t
}: {
  app: any;
  mouseX: any;
  isActive: boolean;
  hoveredApp: string | null;
  setHoveredApp: (id: string | null) => void;
  handleAppClick: (id: string, title: string) => void;
  isLightTheme: boolean;
  t: (key: string) => string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const Icon = app.icon;
  // Calculate distance from mouse to icon center
  const distance = useTransform(mouseX, val => {
    const bounds = ref.current?.getBoundingClientRect() ?? {
      x: 0,
      width: 48
    };
    const iconCenterX = bounds.x + bounds.width / 2;
    return Math.abs(val - iconCenterX);
  });
  // Scale based on distance (closer = larger)
  const widthSync = useTransform(distance, [0, 150], [64, 48]);
  const width = useSpring(widthSync, {
    stiffness: 200,
    damping: 28
  });
  // Y offset based on scale
  const ySync = useTransform(distance, [0, 150], [-16, 0]);
  const y = useSpring(ySync, {
    stiffness: 200,
    damping: 28
  });
  return <div ref={ref} className="relative" data-id="element-214">
      {/* Tooltip */}
      <AnimatePresence data-id="element-215">
        {hoveredApp === app.id && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 5,
        scale: 0.9
      }} transition={{
        duration: 0.15
      }} className={`absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-mono tracking-wide whitespace-nowrap shadow-xl pointer-events-none border ${isLightTheme ? 'bg-white text-[#1A1A1A] border-black/[0.06]' : 'bg-[#1A1A1A] text-[#E8E8E6] border-white/[0.06]'}`} data-id="element-216">
            {t(`${app.id}.title`)}
          </motion.div>}
      </AnimatePresence>

      <motion.button onClick={() => handleAppClick(app.id, t(`${app.id}.title`))} onHoverStart={() => setHoveredApp(app.id)} onHoverEnd={() => setHoveredApp(null)} style={{
      width,
      height: width,
      y
    }} whileTap={{
      scale: 0.95
    }} transition={{
      type: 'spring',
      bounce: 0.4,
      duration: 0.4
    }} className={`rounded-xl flex items-center justify-center transition-all duration-300 ${isActive ? isLightTheme ? 'bg-black/5 text-[#1A1A1A] shadow-sm' : 'bg-white/10 text-[#E8E8E6] shadow-[0_0_15px_rgba(255,255,255,0.05)]' : isLightTheme ? 'hover:bg-black/5 text-[#6B6B6B]' : 'hover:bg-white/10 text-[#9B9B9B]'}`} data-id="element-217">
        <Icon className="w-5 h-5" strokeWidth={1.5} data-id="element-218" />
      </motion.button>

      {/* Active Indicator */}
      {isActive && <motion.div initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0,
      opacity: 0
    }} className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#C8956C] shadow-[0_0_8px_rgba(200,149,108,0.8)]" data-id="element-219" />}
    </div>;
}